import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { PaginationJSON } from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";

export interface ColumnSort {
  desc: boolean;
  id: string;
}

export interface ColumnFilter {
  id: string;
  value: unknown;
}

/**
 * The columns `GET super-user/search-users` can filter on. Each id is both the table's column id
 * and the query parameter the API binds, so a column outside this list never reaches the request.
 */
const FILTERABLE_COLUMNS = ["email", "firstName", "lastName", "companyName"] as const;

export type SearchUsersFilterField = (typeof FILTERABLE_COLUMNS)[number];

function isFilterableColumn(id: string): id is SearchUsersFilterField {
  return (FILTERABLE_COLUMNS as readonly string[]).includes(id);
}

export type SearchUsersAPIRequestJSON = PaginationJSON & {
  query: string;
  sort?: string;
  filters: Partial<Record<SearchUsersFilterField, string>>;
};

function toFilters(columnFilters: readonly ColumnFilter[]): Partial<Record<SearchUsersFilterField, string>> {
  const filters: Partial<Record<SearchUsersFilterField, string>> = {};
  for (const filter of columnFilters) {
    const value = typeof filter.value === "string" ? filter.value.trim() : "";
    if (isFilterableColumn(filter.id) && value) filters[filter.id] = value;
  }
  return filters;
}

export default class SearchUsersAPIRequest extends PaginationParameters {
  public readonly query: string;
  public readonly sort?: string;
  public readonly filters: Readonly<Partial<Record<SearchUsersFilterField, string>>>;

  public constructor(
    query: string,
    pageIndex: number,
    pageSize: number,
    sort?: ColumnSort,
    columnFilters: readonly ColumnFilter[] = [],
  ) {
    super({ pageIndex, pageSize });
    this.query = query;
    if (sort) {
      this.sort = `${sort?.id},${sort?.desc ? "desc" : "asc"}`;
    } else {
      this.sort = "lastName,asc";
    }
    this.filters = toFilters(columnFilters);
  }

  /**
   * RTK Query keys its cache on the serialized argument, so this carries every field the request
   * sends. The inherited paging-only form put every search for one page in one cache entry (#6489).
   */
  public override toJSON(): SearchUsersAPIRequestJSON {
    return { ...super.toJSON(), query: this.query, sort: this.sort, filters: { ...this.filters } };
  }

  public toUrl(route: string, apiBaseUrl: string): string {
    const url = new URL(route, apiBaseUrl);
    if (this.query) {
      url.searchParams.append("query", this.query);
    }
    if (this.pageIndex !== undefined) {
      url.searchParams.append("pageIndex", this.pageIndex.toString());
    }
    if (this.pageSize) {
      url.searchParams.append("pageSize", this.pageSize.toString());
    }
    if (this.sort) {
      url.searchParams.append("sort", this.sort);
    }
    for (const [field, value] of Object.entries(this.filters)) {
      url.searchParams.append(field, value);
    }
    return url.toString();
  }
}
